/**
 * AnamnesePage - Hauptformular fuer externe Anamnese
 *
 * Features:
 * - Mehrseitige Formularfuehrung
 * - Medikamentenliste fotografieren
 * - AES-256-GCM + RSA-OAEP Verschluesselung mit AAD
 * - Draft Auto-Save (localStorage + verschluesselt auf Server)
 * - Unterschrift
 * - Backend: Hetzner FastAPI
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { submitAnamnese } from '../services/api';
import { encryptAnamneseData, isEncryptionSupported } from '../services/encryption';
import {
  loadFromLocalStorage,
  clearLocalStorage,
  setupAutoSave,
  hasServerDraft,
} from '../services/draftService';
import { questionGroups, shouldShowQuestion, shouldShowSection, stammdatenNeupatient, contactQuestions, adaptQuestionForMinor } from '../utils/questions';
import MedicationCapture from '../components/MedicationCapture/MedicationCapture';
import SignaturePad from '../components/SignaturePad/SignaturePad';
import GuardianConfirmation from '../components/GuardianConfirmation/GuardianConfirmation';
import AdditionalDocuments from '../components/AdditionalDocuments/AdditionalDocuments';

const AnamnesePage = ({ submissionData }) => {
  const navigate = useNavigate();
  const {
    token,
    publicKey,
    keyVersion,
    patientId,
    terminDatum,
    isNewPatient,
    patientName,
    patientGeburtsdatum,
    patientEmail,
    patientGeschlecht,
    lokalanaesthesie,
    ausfallhonorar
  } = submissionData;

  // Form State
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => {
    const initial = {};
    if (patientEmail) initial.email = patientEmail;
    return initial;
  });
  const [medicationPhoto, setMedicationPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Minderjaehrigen-State
  const [isMinor, setIsMinor] = useState(false);
  const [patientAge, setPatientAge] = useState(null);
  const [guardianConfirmed, setGuardianConfirmed] = useState(false);
  const [guardianData, setGuardianData] = useState(null);

  // Dokumente-State
  const [acceptedDocs, setAcceptedDocs] = useState({});
  const [currentDocIndex, setCurrentDocIndex] = useState(0);

  // Draft-State
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [localDraft, setLocalDraft] = useState(null);

  // Ref fuer Auto-Save (damit die Closure immer aktuelle Werte hat)
  const stateRef = useRef({ formData, medicationPhoto, acceptedDocs, guardianData });
  useEffect(() => {
    stateRef.current = { formData, medicationPhoto, acceptedDocs, guardianData };
  }, [formData, medicationPhoto, acceptedDocs, guardianData]);

  const requiredDocs = [];
  if (!lokalanaesthesie) requiredDocs.push('lokalanaesthesie');
  if (!ausfallhonorar) requiredDocs.push('ausfallhonorar');

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (!isNewPatient && patientGeburtsdatum) {
      const age = calculateAge(patientGeburtsdatum);
      setPatientAge(age);
      setIsMinor(age !== null && age < 18);
    }
  }, [isNewPatient, patientGeburtsdatum]);

  // Draft-Check beim Laden
  useEffect(() => {
    const checkDraft = async () => {
      const draft = loadFromLocalStorage(token);
      if (draft) {
        setLocalDraft(draft);
        setShowDraftDialog(true);
        return;
      }
      const serverDraftExists = await hasServerDraft(token);
      if (serverDraftExists) {
        setLocalDraft(null);
        setShowDraftDialog(true);
        return;
      }
      setDraftLoaded(true);
    };
    checkDraft();
  }, [token]);

  // Auto-Save Setup (nach Draft-Check)
  useEffect(() => {
    if (!draftLoaded || !publicKey) return;

    const cleanup = setupAutoSave(
      token,
      () => stateRef.current,
      publicKey,
      patientId,
      keyVersion,
      30000
    );
    return cleanup;
  }, [draftLoaded, token, publicKey, patientId, keyVersion]);

  const patientContext = {
    age: patientAge ?? (formData.geburtsdatum ? calculateAge(formData.geburtsdatum) : null),
    geschlecht: patientGeschlecht || formData.geschlecht || null
  };

  const hasVisibleQuestionsInStep = (groupData) => {
    if (!groupData) return false;
    const hasDirectQuestions = groupData.questions?.some(q =>
      shouldShowQuestion(q, formData, patientContext)
    );
    if (hasDirectQuestions) return true;
    return groupData.sections?.some(section => {
      if (!shouldShowSection(section, patientContext)) return false;
      return section.questions?.some(q =>
        shouldShowQuestion(q, formData, patientContext)
      );
    });
  };

  const steps = [
    { id: 'stammdaten', label: isNewPatient ? 'Persoenliche Daten' : 'Kontaktdaten' },
    ...questionGroups.slice(1)
      .filter(g => hasVisibleQuestionsInStep(g.data))
      .map(g => ({ id: g.id, label: g.title })),
    ...(requiredDocs.length > 0 ? [{ id: 'documents', label: 'Aufklaerungen' }] : []),
    { id: 'signature', label: 'Unterschrift' }
  ];

  const stammdatenQuestions = isNewPatient ? stammdatenNeupatient : contactQuestions;

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleMedicationPhoto = useCallback((photoData) => {
    setMedicationPhoto(photoData);
  }, []);

  const handleSignature = useCallback((signatureData) => {
    setSignature(signatureData);
  }, []);

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    const errors = {};
    let questionsToValidate = [];

    if (step.id === 'intro') return { valid: true, errors: {} };

    if (step.id === 'stammdaten') {
      stammdatenQuestions.sections?.forEach(section => {
        section.questions?.forEach(q => {
          if (shouldShowQuestion(q, formData, patientContext)) questionsToValidate.push(q);
        });
      });
      stammdatenQuestions.questions?.forEach(q => {
        if (shouldShowQuestion(q, formData, patientContext)) questionsToValidate.push(q);
      });
    } else {
      const questionGroup = questionGroups.find(g => g.id === step.id);
      if (questionGroup) {
        const groupData = questionGroup.data;
        groupData.questions?.forEach(q => {
          if (shouldShowQuestion(q, formData, patientContext)) questionsToValidate.push(q);
        });
        groupData.sections?.forEach(section => {
          if (shouldShowSection(section, patientContext)) {
            section.questions?.forEach(q => {
              if (shouldShowQuestion(q, formData, patientContext)) questionsToValidate.push(q);
            });
          }
        });
      }
    }

    questionsToValidate.forEach(q => {
      if (q.required === false) return;
      if (q.dependent && !shouldShowQuestion(q, formData, patientContext)) return;

      if (q.type === 'plz_ort') {
        const plzField = q.plzField || 'plz';
        const ortField = q.ortField || 'ort';
        if (!formData[plzField]) errors[plzField] = 'Pflichtfeld';
        if (!formData[ortField]) errors[ortField] = 'Pflichtfeld';
        return;
      }

      const value = formData[q.field];
      if (value === undefined || value === null || value === '') {
        errors[q.field] = 'Pflichtfeld';
      }
    });

    return { valid: Object.keys(errors).length === 0, errors };
  };

  const handleNext = () => {
    const { valid, errors } = validateCurrentStep();
    if (!valid) {
      setValidationErrors(errors);
      window.scrollTo(0, 0);
      return;
    }
    setValidationErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setValidationErrors({});
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Formular absenden — verschluesseln mit AAD und an Hetzner API senden
  const handleSubmit = async () => {
    if (!signature) {
      setError('Bitte unterschreiben Sie das Formular.');
      return;
    }

    if (!isEncryptionSupported()) {
      setError('Ihr Browser unterstuetzt keine sichere Verschluesselung. Bitte verwenden Sie einen aktuellen Browser.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const dataToEncrypt = {
        anamnese_data: formData,
        signature: signature,
        medication_photo: medicationPhoto || null,
        submitted_at: new Date().toISOString(),
        accepted_documents: acceptedDocs,
        ...(isMinor && guardianData && {
          guardian_info: {
            guardian_name: guardianData.guardianName,
            relationship: guardianData.relationship,
            confirmed: guardianData.confirmed,
            confirmation_timestamp: guardianData.timestamp
          },
          is_minor: true,
          patient_age: patientAge
        })
      };

      // Verschluesseln mit AES-256-GCM + RSA-OAEP + AAD
      const encrypted = await encryptAnamneseData(
        dataToEncrypt, publicKey, patientId, token, keyVersion
      );

      // An Hetzner API senden
      await submitAnamnese({
        token,
        encrypted_data: encrypted.encrypted_data,
        encrypted_key: encrypted.encrypted_key,
        iv: encrypted.iv,
        key_version: encrypted.key_version,
      });

      // Draft aufraumen
      clearLocalStorage(token);

      // Zur Erfolgsseite
      sessionStorage.setItem('anamnese_submitted', 'true');
      if (terminDatum) {
        sessionStorage.setItem('termin_datum', terminDatum);
      }
      navigate('/success');

    } catch (err) {
      console.error('Fehler beim Absenden:', err);
      if (err.message?.includes('verschluesselt') || err.message?.includes('encrypt')) {
        setError('Verschluesselungsfehler. Bitte verwenden Sie einen aktuellen Browser.');
      } else {
        setError(err.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Frage rendern
  const renderQuestion = (question) => {
    if (!shouldShowQuestion(question, formData, patientContext)) return null;

    const adaptedQuestion = adaptQuestionForMinor(question, isMinor);
    const value = formData[adaptedQuestion.field] ?? '';

    switch (adaptedQuestion.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <TextField
            key={adaptedQuestion.field}
            fullWidth
            label={adaptedQuestion.question}
            type={adaptedQuestion.type === 'tel' ? 'tel' : 'text'}
            value={value}
            onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            placeholder={adaptedQuestion.placeholder}
            helperText={validationErrors[adaptedQuestion.field] || adaptedQuestion.helpText}
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            margin="normal"
            inputProps={{ inputMode: adaptedQuestion.inputMode }}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={adaptedQuestion.field}
            fullWidth
            multiline
            rows={4}
            label={adaptedQuestion.question}
            value={value}
            onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            placeholder={adaptedQuestion.placeholder}
            helperText={validationErrors[adaptedQuestion.field] || adaptedQuestion.helpText}
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            margin="normal"
          />
        );

      case 'number':
        return (
          <TextField
            key={adaptedQuestion.field}
            fullWidth
            type="number"
            label={adaptedQuestion.question}
            value={value}
            onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            placeholder={adaptedQuestion.placeholder}
            helperText={validationErrors[adaptedQuestion.field] || adaptedQuestion.helpText}
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            margin="normal"
            inputProps={{ step: adaptedQuestion.step || 1 }}
          />
        );

      case 'date':
        return (
          <TextField
            key={adaptedQuestion.field}
            fullWidth
            type="date"
            label={adaptedQuestion.question}
            value={value}
            onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            helperText={validationErrors[adaptedQuestion.field]}
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            margin="normal"
            slotProps={{ inputLabel: { shrink: true } }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
          />
        );

      case 'yesno':
        return (
          <FormControl
            key={adaptedQuestion.field}
            component="fieldset"
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            sx={{ my: 2, width: '100%' }}
          >
            <Typography
              variant="body1"
              sx={{ mb: 1 }}
              color={validationErrors[adaptedQuestion.field] ? 'error' : 'inherit'}
            >
              {adaptedQuestion.question} {adaptedQuestion.required !== false && '*'}
            </Typography>
            {adaptedQuestion.helpText && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {adaptedQuestion.helpText}
              </Typography>
            )}
            <RadioGroup
              row
              value={value === true ? 'ja' : value === false ? 'nein' : ''}
              onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value === 'ja')}
            >
              <FormControlLabel value="ja" control={<Radio />} label="Ja" />
              <FormControlLabel value="nein" control={<Radio />} label="Nein" />
            </RadioGroup>
            {validationErrors[adaptedQuestion.field] && (
              <Typography variant="caption" color="error">
                {validationErrors[adaptedQuestion.field]}
              </Typography>
            )}
          </FormControl>
        );

      case 'radio':
        return (
          <FormControl
            key={adaptedQuestion.field}
            component="fieldset"
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
            sx={{ my: 2, width: '100%' }}
          >
            {adaptedQuestion.question && (
              <Typography
                variant="body1"
                sx={{ mb: 1 }}
                color={validationErrors[adaptedQuestion.field] ? 'error' : 'inherit'}
              >
                {adaptedQuestion.question} {adaptedQuestion.required !== false && '*'}
              </Typography>
            )}
            <RadioGroup
              value={value}
              onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            >
              {adaptedQuestion.options?.map((option) => (
                <Box key={option.value}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                  />
                  {option.hasTextField && value === option.value && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={formData[option.textField?.field] || ''}
                      onChange={(e) => handleFieldChange(option.textField?.field, e.target.value)}
                      placeholder={option.textField?.placeholder}
                      helperText={option.textField?.helpText}
                      sx={{ ml: 4, mt: 1, maxWidth: 'calc(100% - 32px)' }}
                    />
                  )}
                  {option.hasMedicationCapture && value === option.value && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <MedicationCapture onCapture={handleMedicationPhoto} />
                    </Box>
                  )}
                </Box>
              ))}
            </RadioGroup>
            {validationErrors[adaptedQuestion.field] && (
              <Typography variant="caption" color="error">
                {validationErrors[adaptedQuestion.field]}
              </Typography>
            )}
          </FormControl>
        );

      case 'select':
        return (
          <FormControl
            key={adaptedQuestion.field}
            fullWidth
            margin="normal"
            error={!!validationErrors[adaptedQuestion.field]}
            required={adaptedQuestion.required !== false}
          >
            <InputLabel>{adaptedQuestion.question}</InputLabel>
            <Select
              value={value}
              label={adaptedQuestion.question}
              onChange={(e) => handleFieldChange(adaptedQuestion.field, e.target.value)}
            >
              {adaptedQuestion.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {validationErrors[adaptedQuestion.field] && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {validationErrors[adaptedQuestion.field]}
              </Typography>
            )}
          </FormControl>
        );

      case 'plz_ort': {
        const plzField = adaptedQuestion.plzField || 'plz';
        const ortField = adaptedQuestion.ortField || 'ort';
        return (
          <Box key={adaptedQuestion.field} sx={{ display: 'flex', gap: 2, my: 2 }}>
            <TextField
              sx={{ flex: '0 0 120px' }}
              label="PLZ"
              value={formData[plzField] || ''}
              onChange={(e) => handleFieldChange(plzField, e.target.value)}
              helperText={validationErrors[plzField]}
              error={!!validationErrors[plzField]}
              required={adaptedQuestion.required !== false}
              inputProps={{ inputMode: 'numeric', maxLength: 5 }}
            />
            <TextField
              sx={{ flex: 1 }}
              label="Ort"
              value={formData[ortField] || ''}
              onChange={(e) => handleFieldChange(ortField, e.target.value)}
              helperText={validationErrors[ortField]}
              error={!!validationErrors[ortField]}
              required={adaptedQuestion.required !== false}
            />
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const renderSection = (section) => {
    if (!shouldShowSection(section, patientContext)) return null;
    const hasVisibleQuestions = section.questions?.some(q =>
      shouldShowQuestion(q, formData, patientContext)
    );
    if (!hasVisibleQuestions) return null;

    return (
      <Box key={section.title} sx={{ mb: 3 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          {section.title}
        </Typography>
        {section.questions?.map(q => renderQuestion(q))}
      </Box>
    );
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    if (step.id === 'stammdaten') {
      return (
        <Box sx={{ py: 2 }}>
          {isNewPatient && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Bitte geben Sie Ihre vollstaendigen Daten ein, damit wir Sie als neuen Patienten aufnehmen koennen.
            </Alert>
          )}
          {stammdatenQuestions.sections?.map(section => renderSection(section))}
          {stammdatenQuestions.questions?.map(q => renderQuestion(q))}
        </Box>
      );
    }

    if (step.id === 'documents' && requiredDocs.length > 0) {
      const currentDoc = requiredDocs[currentDocIndex];
      return (
        <AdditionalDocuments
          type={currentDoc}
          patientName={patientName}
          onBack={() => {
            if (currentDocIndex > 0) {
              setCurrentDocIndex(currentDocIndex - 1);
            } else {
              handleBack();
            }
          }}
          onAccept={(docType) => {
            setAcceptedDocs(prev => ({
              ...prev,
              [docType]: { accepted: true, timestamp: new Date().toISOString() }
            }));
            if (currentDocIndex < requiredDocs.length - 1) {
              setCurrentDocIndex(currentDocIndex + 1);
            } else {
              handleNext();
            }
          }}
        />
      );
    }

    if (step.id === 'signature') {
      const signatureTitle = isMinor && guardianData
        ? `Unterschrift ${guardianData.guardianName} (${guardianData.relationship} von ${patientName})`
        : patientName && !isNewPatient
          ? `Unterschrift ${patientName}`
          : 'Ihre Unterschrift';

      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Bestaetigung und Unterschrift
          </Typography>

          <Paper variant="outlined" sx={{ p: 3, my: 3 }}>
            <Typography variant="body1" gutterBottom>
              {isMinor && guardianData
                ? `Hiermit bestaetige ich als ${guardianData.relationship} von ${patientName}, dass ich die Fragen wahrheitsgemaess und nach bestem Wissen beantwortet habe.`
                : 'Hiermit bestaetige ich, dass ich die Fragen wahrheitsgemaess und nach bestem Wissen beantwortet habe.'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mir ist bekannt, dass unvollstaendige oder falsche Angaben
              die Behandlung gefaehrden koennen.
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {signatureTitle}:
          </Typography>

          <SignaturePad onSave={handleSignature} initialSignature={signature} />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      );
    }

    // Fragen-Schritte
    const questionGroup = questionGroups.find(g => g.id === step.id);
    if (!questionGroup) return null;
    const groupData = questionGroup.data;

    return (
      <Box sx={{ py: 2 }}>
        {groupData.questions?.map(q => renderQuestion(q))}
        {groupData.sections?.map(section => renderSection(section))}
      </Box>
    );
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;
  const isDocumentsStep = steps[currentStep]?.id === 'documents';

  // Draft-Dialog
  if (showDraftDialog) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <Paper sx={{ p: 4, maxWidth: 500, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="primary">
            Gespeicherter Entwurf
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {localDraft
              ? `Sie haben einen Entwurf vom ${new Date(localDraft.savedAt).toLocaleString('de-DE')}. Moechten Sie fortfahren?`
              : 'Es existiert ein gespeicherter Entwurf auf dem Server. Leider kann dieser nur in derselben Browser-Sitzung wiederhergestellt werden. Moechten Sie neu beginnen?'
            }
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={() => {
              clearLocalStorage(token);
              setShowDraftDialog(false);
              setDraftLoaded(true);
            }}>
              Neu beginnen
            </Button>
            {localDraft && (
              <Button variant="contained" fullWidth onClick={() => {
                setFormData(localDraft.formData || {});
                if (localDraft.medicationPhoto) setMedicationPhoto(localDraft.medicationPhoto);
                if (localDraft.acceptedDocs) setAcceptedDocs(localDraft.acceptedDocs);
                if (localDraft.guardianData) {
                  setGuardianData(localDraft.guardianData);
                  setGuardianConfirmed(true);
                }
                setShowDraftDialog(false);
                setDraftLoaded(true);
              }}>
                Fortfahren
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  // Guardian-Bestaetigung fuer Minderjaehrige VOR dem Formular
  if (!isNewPatient && isMinor && !guardianConfirmed) {
    return (
      <GuardianConfirmation
        patientName={patientName}
        patientAge={patientAge}
        onConfirm={(data) => {
          setGuardianData(data);
          setGuardianConfirmed(true);
        }}
        onCancel={() => { window.location.href = '/'; }}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8fafc',
        pb: { xs: 12, sm: 4 }
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: { xs: '100%', sm: 600, md: 800 },
          mx: { xs: 0, sm: 'auto' },
          mb: 2,
          background: 'linear-gradient(135deg, #1a6ead 0%, #004c80 100%)',
          color: 'white',
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontWeight: 600,
              color: '#d4af37',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              letterSpacing: '0.5px'
            }}
          >
            Zahnarztpraxis Dr. Hezel
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 2 },
              mt: 1.5
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.95 }}>
              {patientName && !isNewPatient
                ? `Allgemeine Anamnese fuer ${patientName}`
                : 'Allgemeine Anamnese'
              }
            </Typography>

            <Box sx={{
              width: '1px', height: 16, bgcolor: 'rgba(255,255,255,0.4)',
              display: { xs: 'none', sm: 'block' }
            }} />

            <Typography variant="body2" sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              px: 1.5, py: 0.5, borderRadius: 2, fontWeight: 500
            }}>
              {steps[currentStep].label}
            </Typography>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#d4af37',
              transition: 'transform 0.4s ease'
            }
          }}
        />
      </Paper>

      {/* Content */}
      <Paper
        sx={{
          maxWidth: { xs: '100%', sm: 600, md: 800 },
          mx: { xs: 1, sm: 'auto' },
          p: { xs: 2, sm: 3 },
          minHeight: { xs: 350, sm: 400 },
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Bitte fuellen Sie alle Pflichtfelder aus, bevor Sie fortfahren.
          </Alert>
        )}
        {renderStepContent()}
      </Paper>

      {/* Sticky Navigation */}
      {!isDocumentsStep && (
        <Paper
          sx={{
            position: { xs: 'fixed', sm: 'relative' },
            bottom: { xs: 0, sm: 'auto' },
            left: { xs: 0, sm: 'auto' },
            right: { xs: 0, sm: 'auto' },
            maxWidth: { xs: '100%', sm: 600, md: 800 },
            mx: { xs: 0, sm: 'auto' },
            mt: { xs: 0, sm: 2 },
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            borderRadius: { xs: 0, sm: 3 },
            zIndex: 1000,
            boxShadow: { xs: '0 -4px 12px rgba(0,0,0,0.1)', sm: '0 2px 12px rgba(0,0,0,0.08)' },
            borderTop: { xs: '1px solid rgba(0,0,0,0.08)', sm: 'none' }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            disabled={currentStep === 0 || isSubmitting}
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 120 } }}
          >
            Zurueck
          </Button>

          {isLastStep ? (
            <Button
              variant="contained"
              color="primary"
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting || !signature}
              sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 140 } }}
            >
              {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 120 } }}
            >
              Weiter
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AnamnesePage;
